import React, { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { useFormik, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { parseActionTemplateFormValuesToDTO } from 'config/api/actionLibrary/actionLibrary.parsers';
import type { ActionTemplateCreateDTO } from 'config/api/actionLibrary/actionLibrary.types';
import assetCategoriesApi from 'config/api/assetCategories/assetCategories';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useCreateDraftActionTemplate from 'hooks/actionTemplates/useCreateDraftActionTemplate/useCreateDraftActionTemplate';
import useLockActionTemplate from 'hooks/actionTemplates/useLockActionTemplate/useLockActionTemplate';
import usePublishActionTemplate from 'hooks/actionTemplates/usePublishActionTemplate/usePublishActionTemplate';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

import ActionTemplateProperties from './_components/ActionTemplateProperties/ActionTemplateProperties';
import ActionTemplateSteps from './_components/ActionTemplateSteps/ActionTemplateSteps';
import ActionTemplateStepsActions from './_components/ActionTemplateSteps/ActionTemplateStepsActions';
import { ActionTemplateFormValues } from './ActionTemplateForm.types';
import getActionTemplateValidationSchema from './ActionTemplateForm.validation';

type Props = {
  initialValues?: ActionTemplateFormValues;
  isEditMode?: boolean;
  templateId?: number;
  onAllowNavigationBeforePush?: () => void;
  onDirtyStateChange?: (isDirty: boolean) => void;
  onSaveRequestRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onCancelNavigation?: () => Promise<void>;
};

export type ActionTemplateFormRef = {
  isDirty: () => boolean;
  handleSave: () => Promise<void | { id: number }>;
};

const ActionTemplateForm: React.FC<Props> = ({
  initialValues,
  isEditMode = false,
  templateId,
  onAllowNavigationBeforePush,
  onDirtyStateChange,
  onSaveRequestRef,
  onCancelNavigation,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const formikRef = useRef<any>(null);
  const { mutateAsync: createDraft } = useCreateDraftActionTemplate();
  const { mutate: lockTemplate } = useLockActionTemplate();
  const { mutateAsync: publishTemplate } = usePublishActionTemplate();

  useEffect(() => {
    if (isEditMode && templateId) {
      lockTemplate(templateId);
    }
  }, [isEditMode, templateId, lockTemplate]);

  // Define handleSave before formik so it can be used in onSubmit
  const handleSave = async () => {
    if (!formikRef.current) {
      throw new Error('Formik not initialized');
    }
    const formik = formikRef.current;

    // Mark checklist as touched to show validation error if empty
    formik.setFieldTouched('checklist', true);

    // Validate form before saving
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // Mark all fields as touched to show validation errors
      Object.keys(errors).forEach(field => {
        formik.setFieldTouched(field, true);
      });
      throw new Error('Form validation failed');
    }

    try {
      setLoading(true);

      const dto = parseActionTemplateFormValuesToDTO(formik.values);

      if (!isEditMode) {
        dto.status = 'inactive';
      }

      if (isEditMode && templateId) {
        // Same flow as Save & continue: draft + lock (no PATCH)
        await createDraft({ id: templateId, data: dto as ActionTemplateCreateDTO });
        await actionTemplatesApi.lockActionTemplate(templateId);
        await queryClient.invalidateQueries(['actionTemplate', templateId.toString()]);
        enqueueSnackbar(t(action_library_messages.success.updated), { variant: 'success' });
        // Reset form dirty state after successful save
        formik.resetForm({ values: formik.values });
        return { id: templateId };
      }

      const { data } = await actionTemplatesApi.createActionTemplate(dto as ActionTemplateCreateDTO);
      enqueueSnackbar(t(action_library_messages.success.created), { variant: 'success' });
      // Reset form dirty state after successful save
      formik.resetForm({ values: formik.values });
      return { id: data.id };
    } catch (error: unknown) {
      let errorMessage = isEditMode ? t('Failed to update action template') : t('Failed to create action template');

      if (axios.isAxiosError(error) && error.response?.status === 403) {
        errorMessage = t(action_library_messages.errors.edit_permission_denied);
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<ActionTemplateFormValues>({
    enableReinitialize: true,
    initialValues: initialValues || {
      purpose: 'assets',
      area: '',
      subarea: '',
      title: '',
      shortDescription: '',
      criticality: 'medium',
      tags: [],
      referenceRegulation: [],
      reportedTo: '',
      assetTypes: [],
      checklist: [],
      validityPeriod: '',
      validityPeriodDoesNotApply: false,
    },
    validationSchema: getActionTemplateValidationSchema(t),
    onSubmit: async (values, { setSubmitting }) => {
      let idToPublish: number | undefined;
      try {
        const form = formikRef.current;
        if (!form) throw new Error('Formik not initialized');

        // Validate before Save & Publish
        form.setFieldTouched('checklist', true);
        const errors = await form.validateForm();
        if (Object.keys(errors).length > 0) {
          Object.keys(errors).forEach(field => form.setFieldTouched(field, true));
          throw new Error('Form validation failed');
        }

        const dto = parseActionTemplateFormValuesToDTO(values);

        if (isEditMode && templateId) {
          // Edit: only POST draft then POST publish — do not PATCH the template
          idToPublish = templateId;
        } else {
          // Create: handleSave does POST create and returns new id
          const result = await handleSave();
          idToPublish = result?.id;
        }

        if (idToPublish) {
          setLoading(true);
          // Create: template must be locked before saving draft (edit: already locked on page load)
          if (!isEditMode) {
            await actionTemplatesApi.lockActionTemplate(idToPublish);
          }
          // Same flow for create and edit: POST draft then POST publish
          await createDraft({ id: idToPublish, data: dto as ActionTemplateCreateDTO });
          await publishTemplate({ id: idToPublish, data: { confirmation: true } });
          if (isEditMode && templateId) {
            enqueueSnackbar(t(action_library_messages.success.updated), { variant: 'success' });
          }
          // Create: success snackbar already shown in handleSave()
          onAllowNavigationBeforePush?.();
          history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', idToPublish.toString()));
        }
      } catch (error: unknown) {
        let errorMessage = t(action_library_messages.errors.template_status_update_failed);

        if (axios.isAxiosError(error) && error.response?.status === 403) {
          errorMessage = t(action_library_messages.errors.edit_permission_denied);
        }

        // Show error when draft/publish failed (edit always; create only when handleSave already succeeded)
        if ((isEditMode && templateId) || idToPublish !== undefined) {
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  // Store formik in ref for handleSave
  formikRef.current = formik;

  // Expose save handler to parent via ref
  useEffect(() => {
    if (onSaveRequestRef) {
      // eslint-disable-next-line no-param-reassign
      onSaveRequestRef.current = async (): Promise<void> => {
        await handleSave();
      };
    }
  }, [onSaveRequestRef, handleSave]);

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyStateChange]);

  const { handleSubmit } = formik;

  // Fetch choices and areas from API
  const { data: choicesData } = useQuery('actionTemplatesChoices', () => actionTemplatesApi.getChoices(), {
    staleTime: 1000 * 60 * 10,
  });

  // Fetch areas with subareas from API
  const { data: areasData } = useQuery('actionTemplatesAreas', () => actionTemplatesApi.getAreas(), {
    staleTime: 1000 * 60 * 10,
  });

  // Fetch asset types
  const { data: assetTypes } = useQuery(QUERY_KEYS.ASSETS_CATEGORIES, assetCategoriesApi.getAllAssetCategoriesForSelect, {
    staleTime: 1000 * 60 * 10,
  });

  // Check if checklist has validation error
  const checklistError = formik.errors.checklist;
  const checklistTouched = formik.touched.checklist;
  const showChecklistError = checklistTouched && Boolean(checklistError) && formik.values.checklist.length === 0;

  // Handler to add new step
  const handleAddStep = () => {
    const currentChecklist = formik.values.checklist || [];
    const newStepId = uuidv4();
    formik.setFieldValue('checklist', [
      ...currentChecklist,
      { id: newStepId, title: '', instruction: '', isMandatory: true, relatedReading: '', relevantFor: '' },
    ]);

    // Scroll to the newly created step after React renders it
    setTimeout(() => {
      const newStepElement = document.querySelector(`[data-step-id="${newStepId}"]`);
      if (newStepElement) {
        newStepElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handler for cancel/back button
  const handleCancel = async () => {
    if (onCancelNavigation) {
      // Use the provided navigation handler (with unsaved changes protection)
      await onCancelNavigation();
    } else if (isEditMode && templateId) {
      // Fallback to direct navigation if no handler provided
      history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', templateId.toString()));
    } else {
      history.push(PATHS.ACTION_LIBRARY);
    }
  };

  const handleSaveAndContinue = async () => {
    // Validate form before saving
    formik.setFieldTouched('checklist', true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach(field => {
        formik.setFieldTouched(field, true);
      });
      return;
    }

    try {
      setLoading(true);
      const dto = parseActionTemplateFormValuesToDTO(formik.values);

      if (!isEditMode) {
        dto.status = 'inactive';
        // 1. Create action-template
        const { data: createdTemplate } = await actionTemplatesApi.createActionTemplate(dto as ActionTemplateCreateDTO);
        const newId = createdTemplate.id;

        // 2. Lock it
        await actionTemplatesApi.lockActionTemplate(newId);

        // 3. Save as draft (required before publish – backend needs a draft to publish)
        await createDraft({ id: newId, data: dto as ActionTemplateCreateDTO });

        // 4. Publish so the template is visible to all Global Administrators (even when Inactive)
        await publishTemplate({ id: newId, data: { confirmation: true } });

        enqueueSnackbar(t(action_library_messages.success.created), { variant: 'success' });

        // Reset form dirty state so Back doesn't show Unsaved changes modal
        formik.resetForm({ values: formik.values });

        // Update URL to edit mode without full reload if possible, or push to edit route
        // Since we want to stay on page, we switch to edit mode URL
        history.replace(PATHS.ACTION_LIBRARY_EDIT.replace(':id', newId.toString()));
      } else if (templateId) {
        // Save as draft
        await createDraft({ id: templateId, data: dto as ActionTemplateCreateDTO });

        // Lock it
        await actionTemplatesApi.lockActionTemplate(templateId);

        enqueueSnackbar(t(action_library_messages.success.updated), { variant: 'success' });
        // Reset form dirty state only in edit mode (we stay on page)
        formik.resetForm({ values: formik.values });
      }
    } catch (error) {
      enqueueSnackbar(
        isEditMode ? t(action_library_messages.errors.template_update_failed) : t(action_library_messages.errors.template_create_failed),
        { variant: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  const assetTypesOptions = React.useMemo(() => assetTypes || [], [assetTypes]);

  const handleSubareaCreated = useCallback(
    (newSubareaId: number) => {
      formik.setFieldValue('subarea', newSubareaId);
      queryClient.invalidateQueries('actionTemplatesAreas');
    },
    [formik, queryClient],
  );

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <ActionTemplateStepsActions
          isEditMode={isEditMode}
          loading={loading}
          onAddStep={handleAddStep}
          onCancel={handleCancel}
          onConfirmSaveAndPublish={formik.handleSubmit}
          onSaveAndContinue={handleSaveAndContinue}
          showChecklistError={Boolean(showChecklistError)}
        />
        <ActionTemplateProperties
          areasData={areasData}
          assetTypes={assetTypesOptions}
          choicesData={choicesData}
          formik={formik}
          onSubareaCreated={handleSubareaCreated}
        />
        <ActionTemplateSteps formik={formik} />
      </form>
    </FormikProvider>
  );
};

export default ActionTemplateForm;
